import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import styles from './ConfirmLeadData.module.css';
import { CarDTO, ConsentDTO, CustomTemporaryDataResourceService, TempAndCarDTO, TemporaryDataDTO } from '@/openapi';
import { useRemoteGet } from '@/hooks/useRemoteGet';
import Loader from '@/components/UI/Loader';
import { useRemotePost } from '@/hooks/useRemotePost';
import { useTranslation } from 'react-i18next';

const ConfirmLeadData = () => {
	const { t } = useTranslation();

	const [searchParams] = useSearchParams();
	const tempId = searchParams.get('tempId');

	const [tempData, setTempData] = useState<TemporaryDataDTO | undefined>(undefined);
	const [offer, setOffer] = useState<CarDTO | undefined>(undefined);
	const [error, setError] = useState<string | null>(null);
	const [consentSubmitted, setConsentSubmitted] = useState<boolean>(false);
	const [confirmationState, setConfirmationState] = useState<{
		success: boolean;
		failure: boolean;
		message: string | null;
	}>({
		success: false,
		failure: false,
		message: null
	});

	const findTempData = useRemoteGet<TempAndCarDTO>({
		remoteMethod: async () => {
			if (!tempId) throw new Error('lead data not available');

			return CustomTemporaryDataResourceService.getTemporaryDataDetails({
				tempId: parseInt(tempId)
			});
		},
		lazy: true
	});

	const confirmConsent = useRemotePost<void, { tempId: number; requestBody: ConsentDTO }>({
		remoteMethod: CustomTemporaryDataResourceService.dataConfirmation,
		lazy: true
	});

	const handleConsent = async (consent: boolean) => {
		if (!tempId) {
			setError(t('leadDataConfirmConsent.errMissingData'));
			return;
		}

		setConsentSubmitted(true);
		setConfirmationState({ success: false, failure: false, message: null });

		const consentObj: ConsentDTO = {
			consent: consent,
			source: undefined
		};

		confirmConsent.fetch({ tempId: parseInt(tempId), requestBody: consentObj });
	};

	useEffect(() => {
		if (tempId) findTempData.fetch();
		else setError(t('leadDataConfirmConsent.errMissingData'));

		setConfirmationState({ success: false, failure: false, message: null });
	}, [tempId]);

	useEffect(() => {
		if (findTempData.data && !findTempData.loading) {
			setTempData(findTempData.data.temporaryData);
			setOffer(findTempData.data.car);
		} else if (findTempData.error && !findTempData.loading) {
			console.error('Errore nel recupero dei dati temporanei del lead: ', error);
			setError(t('leadDataConfirmConsent.errUploadingData'));
		}
	}, [findTempData.data, findTempData.error]);

	useEffect(() => {
		// Esce subito se in loading o se non è mai stata fatta una chiamata
		if (confirmConsent.loading || !consentSubmitted) {
			return;
		}

		if (confirmConsent.error) {
			setConfirmationState({
				success: false,
				failure: true,
				message: t('leadDataConfirmConsent.failedMessage')
			});
		} else {
			setConfirmationState({
				success: true,
				failure: false,
				message: t('leadDataConfirmConsent.successfulMessage')
			});
		}
	}, [confirmConsent.loading, confirmConsent.error, consentSubmitted]);

	if (error) {
		return (
			<div className={styles.confirm_data_container}>
				<Navbar className={styles.navDivider} />
				<main className={styles.mainContent}>
					<div className={styles.errorContainer}>
						<h2>{t('leadDataConfirmConsent.errorTitle')}</h2>
						<p>{error}</p>
					</div>
				</main>
				<Footer />
			</div>
		);
	}

	return (
		<div className={styles.confirm_data_container}>
			<Navbar className={styles.navDivider} />

			<main className={styles.mainContent}>
				<Loader visible={findTempData.loading || confirmConsent.loading} />

				<div className={styles.confirmationCard}>
					<div className={styles.cardHeader}>
						<h1>{t('leadDataConfirmConsent.title')}</h1>
						<p>{t('leadDataConfirmConsent.title')}</p>
					</div>

					<div className={styles.cardContent}>
						{tempData && (
							<div className={styles.dataSection}>
								<h2 className={styles.sectionTitle}>{t('leadDataConfirmConsent.leadData.title')}</h2>
								<div className={styles.dataGrid}>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.leadData.name')}</span>
										<span className={styles.dataValue}>
											{tempData.firstname || t('leadDataConfirmConsent.notSpecified')}{' '}
											{tempData.lastname || t('leadDataConfirmConsent.notSpecified')}
										</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.leadData.email')}</span>
										<span className={styles.dataValue}>{tempData.email || t('leadDataConfirmConsent.notSpecified')}</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.leadData.taxCode')}</span>
										<span className={styles.dataValue}>{tempData.taxCode || t('leadDataConfirmConsent.notSpecified')}</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.leadData.vat')}</span>
										<span className={styles.dataValue}>{tempData.nrVAT || t('leadDataConfirmConsent.notSpecified')}</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.leadData.phone')}</span>
										<span className={styles.dataValue}>{tempData.phone || t('leadDataConfirmConsent.notSpecified')}</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.leadData.whatsapp')}</span>
										<span className={styles.dataValue}>{tempData.whatsapp || t('leadDataConfirmConsent.notSpecified')}</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.leadData.city')}</span>
										<span className={styles.dataValue}>
											{tempData.town || t('leadDataConfirmConsent.notSpecified')}{' '}
											{tempData.province || t('leadDataConfirmConsent.notSpecified')}
										</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.leadData.address')}</span>
										<span className={styles.dataValue}>
											{tempData.completeAddress || t('leadDataConfirmConsent.notSpecified')}
										</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.leadData.company')}</span>
										<span className={styles.dataValue}>{tempData.companyName || t('leadDataConfirmConsent.notSpecified')}</span>
									</div>
								</div>
							</div>
						)}

						{offer && (
							<div className={styles.dataSection}>
								<h2 className={styles.sectionTitle}>{t('leadDataConfirmConsent.carData.title')}</h2>
								<div className={styles.dataGrid}>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.carData.model')}</span>
										<span className={styles.dataValue}>
											{offer.brand || t('leadDataConfirmConsent.notSpecified')}{' '}
											{offer.model || t('leadDataConfirmConsent.notSpecified')}
										</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.carData.category')}</span>
										<span className={styles.dataValue}>{offer.category || t('leadDataConfirmConsent.notSpecified')}</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.carData.horsepower')}</span>
										<span className={styles.dataValue}>{offer.horsepower || t('leadDataConfirmConsent.notSpecified')} CV</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.carData.doors')}</span>
										<span className={styles.dataValue}>{offer.doors || t('leadDataConfirmConsent.notSpecified')}</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.carData.prodYear')}</span>
										<span className={styles.dataValue}>{offer.productionYear || t('leadDataConfirmConsent.notSpecified')}</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.carData.totalPrice')}</span>
										<span className={styles.dataValue}>
											€ {offer.totalPrice?.toLocaleString() || t('leadDataConfirmConsent.notSpecified')}
										</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.carData.advance')}</span>
										<span className={styles.dataValue}>
											€ {offer.advance?.toLocaleString() || t('leadDataConfirmConsent.notSpecified')}
										</span>
									</div>
									<div className={styles.dataItem}>
										<span className={styles.dataLabel}>{t('leadDataConfirmConsent.carData.monthPrice')}</span>
										<span className={styles.dataValue}>
											€ {offer.pricePerMonth || t('leadDataConfirmConsent.notSpecified')}/
											{t('leadDataConfirmConsent.carData.month')}
										</span>
									</div>
								</div>
							</div>
						)}

						<div className={styles.actionSection}>
							<div className={styles.buttonGroup}>
								<button
									className={`${styles.button} ${styles.confirmButton}`}
									onClick={() => handleConsent(true)}
									disabled={confirmationState.success}
								>
									{t('leadDataConfirmConsent.confirmBtn')}
								</button>
								<button
									className={`${styles.button} ${styles.rejectButton}`}
									onClick={() => handleConsent(false)}
									disabled={confirmationState.success}
								>
									{t('leadDataConfirmConsent.cancelBtn')}
								</button>
							</div>

							{(confirmationState.failure || confirmationState.success) && confirmationState.message && (
								<div className={`${styles.alert} ${confirmationState.success ? styles.alertSuccess : styles.alertError}`}>
									{confirmationState.message}
								</div>
							)}
						</div>
					</div>
				</div>
			</main>

			<Footer />
		</div>
	);
};

export default ConfirmLeadData;
